import React, { useMemo, CSSProperties } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import useShots from '../../contexts/shots/provider';
import { FIXED_LAYOUTS, PLAIN_LAYOUTS } from '../../utils/video-layout';
import {
	getFeedStyle,
	numberToPercent,
	percMultiply,
	toMap,
} from '../../utils/utils';
import { GreenroomState as GS } from '../../stores/types/types';
import useDropArea from '../../hooks/use-dnd/use-drop-area';
import { DRAG_TYPE } from '../../stores/reducers/user-interface';
import { videoGridClasses } from '../../types/constants';
import type { ShotFeed } from '../../stores/types/shots';
import { FEED_TYPES } from '../../stores/reducers/shots';
import FeedPlaceholderSpotlight from './feed-placeholder-spotlight';
import useCanvasConfig from '../../hooks/use-canvas-config';

interface Props {
    dropCallback: any;
    selectedShotFeeds: ShotFeed[];
	audioOnlyFeeds: ShotFeed[];
	forHidden?: boolean;
	id: string;
}

export default function MainVideoView(props: Props) {
	const {
		dropCallback, forHidden, id
	} = props;
	const canvasStyle = useSelector((state: GS) => state.ShotsReducer.canvasStyle);
	const host = useSelector((state: GS) => state.SessionReducer.host);
	const embed = useSelector((state: GS) => state.SessionReducer.embed);

	const {
		layoutSpacingPresets,
		wallpaperColor,
		presenterVideoBorderPosition,
	} = canvasStyle;

	const { currentShot, nextShot } = useShots();

	const shotToUse = (forHidden && !!nextShot) ? nextShot : currentShot;
	const { shotFeeds, forcedLayout } = useCanvasConfig(shotToUse);

	const selectedShotFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => !shotFeed.audioOnly)
		: []), [shotFeeds]);
	const audioOnlyFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => shotFeed.audioOnly)
		: []), [shotFeeds]);

	const hasPictureInPictureMemo = useMemo(
		() => forcedLayout.includes('picture-in-picture'),
		[forcedLayout],
	);

	function margins() {
		// We created some media specific layouts that have "media" in their type title.
		// These layouts have been calculated for 16:9 and 4:3 media layouts with specific
		// presenter sizes and gaps as well.
		// When these are selected we ignore spacing settings (borders still work though).
		const fixedSpacingLayouts = [...FIXED_LAYOUTS, ...PLAIN_LAYOUTS];
		const isFixedLayout = forcedLayout
			? fixedSpacingLayouts.some((layout) => forcedLayout.includes(layout))
			: false;

		if (isFixedLayout) {
			return {
				columnGap: 0,
				rowGap: 0,
				paddingTop: 0,
				paddingBottom: 0,
				paddingRight: 0,
				paddingLeft: 0,
			};
		}
		const layoutMap = toMap('layout', layoutSpacingPresets);
		// if no layout exists set no margins
		const layout = layoutMap.get(forcedLayout);
		const backup = {
			spacing: {
				top: 0, bottom: 0, side: 0, inner: 0,
			},
		};
		const marginSpacing = layout?.spacing ? layout : backup;
		const {
			inner, top, side, bottom,
		} = marginSpacing.spacing;
		return {
			columnGap: numberToPercent(inner),
			rowGap: percMultiply(numberToPercent(inner) || '0%'),
			paddingTop: numberToPercent(top),
			paddingBottom: numberToPercent(bottom),
			paddingRight: numberToPercent(side),
			paddingLeft: numberToPercent(side),
		};
	}

	const marginMemo = useMemo(() => margins(), [forcedLayout, shotFeeds, layoutSpacingPresets]);
	const mainVideoClassesMemo: any[] = useMemo(() => [
		'main-video',
		{
			backgroundColor: wallpaperColor,
		},
	], [forcedLayout, host, embed, wallpaperColor]);

	const forHiddenId = forHidden ? '-hidden-spotlight-' : '';

	const feedContainers = (index:number, isExcess:boolean, isAudioOnly: boolean) => {
		const audioOnly = isAudioOnly ? 'audio-only' : '';
		const { isOver, dropRef } = useDropArea({
			accepts: DRAG_TYPE.FEEDS,
			dropCallback,
			dropAreaData: { feedPosition: index },
		});
		const mainPictureInPicture = hasPictureInPictureMemo && index === 1 ? {
			top: 0,
			left: 0,
			bottom: 0,
			right: 0,
		} : {};

		const { style, insideStyle } = getFeedStyle(
			forcedLayout,
			index,
			canvasStyle,
		);

		const mainStyle: CSSProperties = {
			display: style.display,
			borderRadius: style.borderRadius,
			boxShadow: !isExcess ? style.boxShadow : 'none',
			position: (isExcess || (hasPictureInPictureMemo && index !== 0)) ? 'absolute' : 'relative',
			zIndex: hasPictureInPictureMemo && index === 0 ? 12 : 11,
			...mainPictureInPicture,
		};

		return (
        <React.Fragment key={`rf-${index}${audioOnly}`}>
            <div
                key={`div-${index}${audioOnly}`}
                className={classNames('video-container-presenter', videoGridClasses[index])}
				style={mainStyle}
            >
                {isOver && <div className="dnd-highlighted" />}
                <div ref={dropRef}
					key={`feed-${index}${audioOnly}`}
					className="feed-drop-area spotlight-container"
					data-index={index}
					id={`feed-${index}${forHiddenId}${audioOnly}`}>
					{(shotFeeds[index]?.feedType === FEED_TYPES.EMPTY)
						&& <FeedPlaceholderSpotlight
						/>}
				</div>
				{presenterVideoBorderPosition === 'inside' && (
					<div style={insideStyle} />
				)}
			</div>
        </React.Fragment>
		);
	};

	const feedsInCanvas = (feeds: ShotFeed[], isAudioOnly:boolean) => {
		const normalFeeds = feeds.map((feed, index) => feedContainers(index, false, isAudioOnly));
		const numberOfFeeds = feeds.length;
		const totalFeeds = isAudioOnly ? 16 : 8;
		for (let i = 0; i < (totalFeeds - numberOfFeeds); i += 1) {
			normalFeeds.push(feedContainers(numberOfFeeds + i, true, isAudioOnly));
		}
		return normalFeeds;
	};

	return (
        <>
			<div className={classNames(mainVideoClassesMemo, 'grid', 'eight', 'audio-only', 'hidden')}>
				{feedsInCanvas(audioOnlyFeeds, true)}
			</div>

            <div 
				style={{ ...marginMemo, zIndex: forHidden ? 0 : 1 }} 
				className={classNames(mainVideoClassesMemo, forcedLayout, 'layer2')}
				id={id}
			>
                {feedsInCanvas(selectedShotFeeds, false)}
            </div>
        </>
	);
}
